export interface ToastPreferences {
    duration: number;
    position: 'top-right' | 'top-left' | 'bottom-right' | 'bottom-left';
    maxToasts: number;
    categories: {
        [key: string]: {
            persist: boolean;
            duration: number;
        };
    };
}

const STORAGE_KEY = 'toast-preferences';
const DEFAULT_PREFERENCES: ToastPreferences = {
    duration: 5000,
    position: 'top-right',
    maxToasts: 5,
    categories: {
        system: {
            persist: true,
            duration: 0, // 0 means persist indefinitely
        },
        notification: {
            persist: false,
            duration: 5000,
        },
        error: {
            persist: true,
            duration: 10000,
        },
        success: {
            persist: false,
            duration: 3000,
        },
    },
};

export function getToastPreferences(): ToastPreferences {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            return DEFAULT_PREFERENCES;
        }

        const preferences = JSON.parse(stored) as ToastPreferences;
        // Merge with defaults to ensure all fields are present
        return {
            ...DEFAULT_PREFERENCES,
            ...preferences,
            categories: {
                ...DEFAULT_PREFERENCES.categories,
                ...preferences.categories,
            },
        };
    } catch (error) {
        console.error('Error loading toast preferences:', error);
        return DEFAULT_PREFERENCES;
    }
}

export function saveToastPreferences(preferences: Partial<ToastPreferences>): void {
    try {
        const current = getToastPreferences();
        const updated = {
            ...current,
            ...preferences,
            categories: {
                ...current.categories,
                ...preferences.categories,
            },
        };
        localStorage.setItem(STORAGE_KEY, JSON.stringify(updated));
    } catch (error) {
        console.error('Error saving toast preferences:', error);
    }
}

export function getToastDuration(category?: string): number {
    const preferences = getToastPreferences();
    if (category && preferences.categories[category]) {
        return preferences.categories[category].duration;
    }
    return preferences.duration;
}

export function shouldPersistToast(category?: string): boolean {
    const preferences = getToastPreferences();
    if (category && preferences.categories[category]) {
        return preferences.categories[category].persist;
    }
    return false;
}

export function getMaxToasts(): number {
    return getToastPreferences().maxToasts;
}

export function getToastPosition(): ToastPreferences['position'] {
    return getToastPreferences().position;
}

export function addToastCategory(
    category: string,
    options: { persist: boolean; duration: number }
): void {
    const preferences = getToastPreferences();
    preferences.categories[category] = options;
    saveToastPreferences(preferences);
}

export function removeToastCategory(category: string): void {
    const preferences = getToastPreferences();
    delete preferences.categories[category];
    saveToastPreferences(preferences);
}
